import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Trash2, Copy, Bell, Layers } from 'lucide-react';
import { consultingApi, stackApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import StackConsultingPrime from '../components/StackConsultingPrime';
import { IMG } from '../lib/images';
import { clearGuestStack, parseGuestStack, saveGuestStack } from '../utils/runningStackStore';
import { buildToolGoUrl } from '../utils/affiliateLinks';
const DEFAULT_ALERT_PREFS = {
  price_change: true,
  better_alternative: true,
  stack_gap: true,
  renewal_reminder: true,
  newsletter_subscription: true,
  email: '',
};

const InfoTag = ({ text }) => (
  <span
    title={text}
    aria-label={text}
    className="inline-flex items-center justify-center w-4 h-4 ml-2 rounded-full border text-[10px]"
    style={{ borderColor: 'rgba(255,255,255,0.28)', color: 'var(--text-muted)' }}
  >
    i
  </span>
);

const mergeAlertPrefs = (prefs) => ({
  ...DEFAULT_ALERT_PREFS,
  ...(prefs || {}),
});

const normalizeGuestStackItems = (items = []) => (
  items.map((item) => {
    const parsedCost = Number.parseFloat(String(item.monthly_cost ?? '').replace(/[^0-9.]/g, ''));
    return {
      ...item,
      notes: item.notes || '',
      monthly_cost: Number.isFinite(parsedCost) ? parsedCost : 0,
      annual_renewal_date: item.annual_renewal_date || null,
    };
  })
);

const StackDashboardPage = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stackData, setStackData] = useState(null);
  const [guestStack, setGuestStack] = useState([]);
  const [syncMessage, setSyncMessage] = useState('');
  const [draftByTool, setDraftByTool] = useState({});
  const [copyingLinks, setCopyingLinks] = useState(false);
  const [runningAlerts, setRunningAlerts] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [savingPublic, setSavingPublic] = useState(false);
  const [alertPrefs, setAlertPrefs] = useState(DEFAULT_ALERT_PREFS);
  const [publicSettings, setPublicSettings] = useState({
    is_public: false,
    username: '',
    role_profile: 'operator',
    company_size: 'small-team',
  });

  const loadStack = useCallback(async () => {
    setLoading(true);
    setError('');

    if (!isAuthenticated) {
      setGuestStack(normalizeGuestStackItems(parseGuestStack()));
      setLoading(false);
      return;
    }

    try {
      const response = await stackApi.getDashboardStack();
      setStackData(response.data);
      setAlertPrefs(mergeAlertPrefs(response.data?.alert_preferences));
      setPublicSettings({
        is_public: !!response.data?.is_public,
        username: response.data?.username || '',
        role_profile: response.data?.role_profile || 'operator',
        company_size: response.data?.company_size || 'small-team',
      });

      const localItems = normalizeGuestStackItems(parseGuestStack());
      if (localItems.length > 0) {
        for (const item of localItems) {
          await stackApi.addStackItem({
            tool_id: item.tool_id,
            notes: item.notes || '',
            monthly_cost: Number(item.monthly_cost || 0),
            annual_renewal_date: item.annual_renewal_date || null,
          });
        }
        clearGuestStack();
        const refreshed = await stackApi.getDashboardStack();
        setStackData(refreshed.data);
        setAlertPrefs(mergeAlertPrefs(refreshed.data?.alert_preferences));
        setPublicSettings({
          is_public: !!refreshed.data?.is_public,
          username: refreshed.data?.username || '',
          role_profile: refreshed.data?.role_profile || 'operator',
          company_size: refreshed.data?.company_size || 'small-team',
        });
        setSyncMessage('Guest stack synced to your account.');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load your stack dashboard');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadStack();
  }, [loadStack]);

  const displayedItems = useMemo(
    () => (isAuthenticated ? (stackData?.items || []) : guestStack),
    [isAuthenticated, stackData, guestStack],
  );

  useEffect(() => {
    const nextDraft = {};
    displayedItems.forEach((item) => {
      nextDraft[item.tool_id] = {
        notes: item.notes || '',
        monthly_cost: Number(item.monthly_cost || 0).toFixed(2),
        annual_renewal_date: item.annual_renewal_date ? String(item.annual_renewal_date).slice(0, 10) : '',
      };
    });
    setDraftByTool(nextDraft);
  }, [displayedItems]);

  const totalMonthlyCost = useMemo(
    () => Number((displayedItems || []).reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0).toFixed(2)),
    [displayedItems],
  );

  const costHint = useMemo(() => {
    if (isAuthenticated && stackData?.cost_optimization_hint) return stackData.cost_optimization_hint;
    if (!displayedItems.length) return null;
    const sorted = [...displayedItems].sort((a, b) => Number(b.monthly_cost || 0) - Number(a.monthly_cost || 0));
    if (sorted.length < 2) return null;
    return {
      current: sorted[0].name,
      alternative: sorted[1].name,
      monthly_saving: Math.max(0, Number(sorted[0].monthly_cost || 0) - Number(sorted[1].monthly_cost || 0)).toFixed(2),
    };
  }, [isAuthenticated, stackData, displayedItems]);

  const publicStackPath = useMemo(() => {
    if (!stackData?.is_public) return '';
    const username = (stackData?.username || '').trim();
    return username ? `/stacks/${username}` : '';
  }, [stackData]);

  const updateItem = async (toolId) => {
    const draft = draftByTool[toolId];
    if (!draft) return;
    const parsedCost = Number.parseFloat(String(draft.monthly_cost).replace(/[^0-9.]/g, ''));
    const monthlyCost = Number.isFinite(parsedCost) ? Math.max(parsedCost, 0) : 0;

    try {
      if (!isAuthenticated) {
        const next = normalizeGuestStackItems(parseGuestStack()).map((item) => (item.tool_id === toolId ? {
          ...item,
          notes: draft.notes,
          monthly_cost: monthlyCost,
          annual_renewal_date: draft.annual_renewal_date || null,
        } : item));
        saveGuestStack(next);
        setGuestStack(next);
        setSyncMessage('Local stack item updated.');
        return;
      }

      await stackApi.updateStackItem(toolId, {
        notes: draft.notes,
        monthly_cost: monthlyCost,
        annual_renewal_date: draft.annual_renewal_date || null,
      });

      setStackData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: (prev.items || []).map((item) => (item.tool_id === toolId ? {
            ...item,
            notes: draft.notes,
            monthly_cost: monthlyCost,
            annual_renewal_date: draft.annual_renewal_date || null,
          } : item)),
        };
      });
    } catch {
      setError('Unable to update stack item right now.');
    }
  };

  const removeItem = async (toolId) => {
    if (!isAuthenticated) {
      const next = normalizeGuestStackItems(parseGuestStack()).filter((item) => item.tool_id !== toolId);
      saveGuestStack(next);
      setGuestStack(next);
      return;
    }

    await stackApi.removeStackItem(toolId);
    await loadStack();
  };

  const copyStackLinks = async () => {
    if (!isAuthenticated) return;
    setCopyingLinks(true);
    try {
      await stackApi.getCopyLinks();
      const maskedLinks = displayedItems
        .map((item) => buildToolGoUrl(item))
        .filter((url) => url && url !== '#');
      const links = maskedLinks.length ? maskedLinks : [];
      if (!links.length) {
        setSyncMessage('No launch links found in this stack yet.');
        return;
      }

      const intentEvents = displayedItems
        .filter((item) => buildToolGoUrl(item) !== '#')
        .map((item) =>
          consultingApi.recordToolPurchaseIntent({
            tool_id: item.tool_id,
            tool_name: item.name,
            destination_url: buildToolGoUrl(item),
            source: 'stack_dashboard_copy_links',
            note: 'User launched stack copy links from dashboard',
          })
        );
      if (intentEvents.length) {
        await Promise.allSettled(intentEvents);
      }

      links.forEach((link, index) => {
        setTimeout(() => window.open(link, '_blank', 'noopener,noreferrer'), index * 700);
      });
      setSyncMessage(`Launching ${links.length} stack links.`);
    } finally {
      setCopyingLinks(false);
    }
  };

  const runAlerts = async () => {
    if (!isAuthenticated) return;
    setRunningAlerts(true);
    try {
      const response = await stackApi.runAlertsCheck();
      const alertCount = (response.data?.alerts || []).length;
      setSyncMessage(
        alertCount
          ? `Smart alerts complete: ${alertCount} trigger(s) found.`
          : 'Smart alerts complete: no triggers found right now.'
      );
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to run smart alerts';
      setSyncMessage(`Error: ${errorMsg}`);
      setError(errorMsg);
    } finally {
      setRunningAlerts(false);
    }
  };

  const saveAlertPreferences = async () => {
    if (!isAuthenticated) return;
    setSavingAlerts(true);
    try {
      const response = await stackApi.updateAlerts(alertPrefs);
      setAlertPrefs(mergeAlertPrefs(response.data || alertPrefs));
      setSyncMessage('Alert preferences saved.');
    } finally {
      setSavingAlerts(false);
    }
  };

  const savePublicSettings = async () => {
    if (!isAuthenticated) return;
    setSavingPublic(true);
    try {
      const response = await stackApi.updatePublicSettings(publicSettings);
      const data = response.data || {};
      setPublicSettings((prev) => ({
        ...prev,
        is_public: !!data.is_public,
        username: data.username || prev.username,
        role_profile: data.role_profile || prev.role_profile,
        company_size: data.company_size || prev.company_size,
      }));
      setStackData((prev) => (prev ? {
        ...prev,
        is_public: !!data.is_public,
        username: data.username || prev.username,
        role_profile: data.role_profile || prev.role_profile,
        company_size: data.company_size || prev.company_size,
      } : prev));
      setSyncMessage(data.is_public ? `Public sharing enabled at ${data.share_url}` : 'Public sharing disabled.');
      setError('');
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || 'Unable to save public profile settings.');
    } finally {
      setSavingPublic(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-code" data-testid="stack-dashboard-page">
      <Helmet>
        <title>My Stack Dashboard | Wowrolla</title>
        <meta
          name="description"
          content="Manage your saved AI stack, notes, monthly cost, and optimization alerts in one place."
        />
      </Helmet>

      <section
        className="relative overflow-hidden mb-6"
        style={{
          backgroundImage: `linear-gradient(rgba(8,8,18,0.74), rgba(8,8,18,0.96)), url(${IMG.hero.stack})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-12 relative z-10">
          <Link to="/" className="inline-flex items-center mb-6 transition-colors text-text-secondary hover:text-cyber-yellow">
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 bg-black/40 border border-laser-green/30 rounded-full">
            <Layers size={14} className="text-laser-green" />
            <span className="text-xs text-laser-green uppercase tracking-widest font-mono">Your stack</span>
          </div>
          <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
            My Stack Dashboard
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Your stack costs ${totalMonthlyCost}/month.
            {costHint ? ` You could save by switching ${costHint.current} to ${costHint.alternative}.` : ''}
          </p>
          {costHint && (
            <p className="text-sm mt-1" style={{ color: 'var(--accent-secondary)' }}>
              Estimated monthly saving: ${costHint.monthly_saving}
            </p>
          )}
          {!isAuthenticated && (
            <p className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
              You are viewing your local guest stack. Create an account to persist and unlock smart alerts and sharing.
            </p>
          )}
          {syncMessage && <p className="mt-3 text-sm" style={{ color: 'var(--accent-primary)' }}>{syncMessage}</p>}
        </div>
      </section>

      <section className="container mx-auto px-6 pb-16">

        {isAuthenticated && (
          <div className="flex flex-wrap gap-3 mb-6">
            <button onClick={copyStackLinks} disabled={copyingLinks} className="px-4 py-3 border inline-flex items-center gap-2 disabled:opacity-60" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}>
              <Copy size={16} /> {copyingLinks ? 'Launching...' : 'Copy This Stack (launch links)'}
            </button>
            <button onClick={runAlerts} disabled={runningAlerts} className="px-4 py-3 border inline-flex items-center gap-2 disabled:opacity-60" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}>
              <Bell size={16} /> {runningAlerts ? 'Running Alerts...' : 'Run Smart Alerts'}
            </button>
            {publicStackPath ? (
              <Link to={publicStackPath} className="px-4 py-3 border inline-flex items-center gap-2" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--accent-primary)' }}>
                Public Stack URL <ExternalLink size={16} />
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setSyncMessage('Enable Public Sharing and save settings to activate your public stack URL.')}
                className="px-4 py-3 border inline-flex items-center gap-2"
                style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-muted)' }}
              >
                Public Stack URL <ExternalLink size={16} />
              </button>
            )}
          </div>
        )}

        <div className="terminal-grid p-0 overflow-hidden">
          {loading && <p className="p-6" style={{ color: 'var(--text-secondary)' }}>Loading your stack...</p>}
          {!loading && error && <p className="p-6" style={{ color: '#ff7777' }}>{error}</p>}
          {!loading && !error && displayedItems.length === 0 && (
            <p className="p-6" style={{ color: 'var(--text-muted)' }}>No saved tools yet. Add tools from any tool page.</p>
          )}
          {!loading && !error && displayedItems.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Tool</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Category</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Monthly Cost</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Your Notes</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.map((item) => (
                  <tr key={item.tool_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {item.slug ? <Link to={`/tools/${item.slug}`} style={{ color: 'var(--accent-primary)' }}>{item.name}</Link> : item.name}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{item.category_name || '-'}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={draftByTool[item.tool_id]?.monthly_cost || '0.00'}
                        onChange={(e) => setDraftByTool((prev) => ({
                          ...prev,
                          [item.tool_id]: { ...prev[item.tool_id], monthly_cost: e.target.value },
                        }))}
                        onBlur={() => updateItem(item.tool_id)}
                        className="w-full px-2 py-1 bg-transparent border border-white/20"
                        style={{ color: 'var(--text-primary)' }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={draftByTool[item.tool_id]?.notes || ''}
                        onChange={(e) => setDraftByTool((prev) => ({
                          ...prev,
                          [item.tool_id]: { ...prev[item.tool_id], notes: e.target.value },
                        }))}
                        onBlur={() => updateItem(item.tool_id)}
                        className="w-full px-2 py-1 bg-transparent border border-white/20"
                        style={{ color: 'var(--text-primary)' }}
                        placeholder="Add personal notes..."
                      />
                      <input
                        type="date"
                        value={draftByTool[item.tool_id]?.annual_renewal_date || ''}
                        onChange={(e) => setDraftByTool((prev) => ({
                          ...prev,
                          [item.tool_id]: { ...prev[item.tool_id], annual_renewal_date: e.target.value },
                        }))}
                        onBlur={() => updateItem(item.tool_id)}
                        className="w-full mt-2 px-2 py-1 bg-transparent border border-white/20"
                        style={{ color: 'var(--text-secondary)' }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => removeItem(item.tool_id)} className="inline-flex items-center gap-1" style={{ color: '#ff7777' }}>
                        <Trash2 size={14} /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {isAuthenticated && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 mt-6">
            <div className="terminal-grid p-4">
              <p className="field-heading mb-3">Public Sharing</p>
              <div className="flex items-center justify-between mb-3">
                <label className="label-pro mb-0 flex items-center">
                  Enable public stack page
                  <InfoTag text="When enabled, your stack can be viewed by anyone with your share link. Disable this anytime to make your stack private again." />
                </label>
                <input
                  type="checkbox"
                  checked={!!publicSettings.is_public}
                  onChange={(e) => setPublicSettings((prev) => ({ ...prev, is_public: e.target.checked }))}
                  className="accent-[#FFE800]"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-pro flex items-center">
                    Public username
                    <InfoTag text="This creates your public URL slug, for example /stacks/your-name. Use a simple and recognizable name." />
                  </label>
                  <input
                    type="text"
                    value={publicSettings.username}
                    onChange={(e) => setPublicSettings((prev) => ({ ...prev, username: e.target.value }))}
                    className="input-pro rounded-none"
                    placeholder="your-public-slug"
                  />
                </div>
                <div>
                  <label className="label-pro flex items-center">
                    Role profile
                    <InfoTag text="Shown on your public stack profile so visitors understand your perspective, such as operator, founder, or consultant." />
                  </label>
                  <input
                    type="text"
                    value={publicSettings.role_profile}
                    onChange={(e) => setPublicSettings((prev) => ({ ...prev, role_profile: e.target.value }))}
                    className="input-pro rounded-none"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="label-pro flex items-center">
                  Company size
                  <InfoTag text="Used to give visitors context about your operating scale when they view your public stack." />
                </label>
                <input
                  type="text"
                  value={publicSettings.company_size}
                  onChange={(e) => setPublicSettings((prev) => ({ ...prev, company_size: e.target.value }))}
                  className="input-pro rounded-none"
                />
              </div>
              <button onClick={savePublicSettings} disabled={savingPublic} className="mt-4 px-4 py-2 border disabled:opacity-60" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}>
                {savingPublic ? 'Saving...' : 'Save Public Settings'}
              </button>
            </div>

            <div className="terminal-grid p-4">
              <p className="field-heading mb-3">Smart Alert Preferences</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!alertPrefs.price_change} onChange={(e) => setAlertPrefs((prev) => ({ ...prev, price_change: e.target.checked }))} className="accent-[#FFE800]" />
                  Price change alerts
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!alertPrefs.better_alternative} onChange={(e) => setAlertPrefs((prev) => ({ ...prev, better_alternative: e.target.checked }))} className="accent-[#FFE800]" />
                  Better alternative alerts
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!alertPrefs.stack_gap} onChange={(e) => setAlertPrefs((prev) => ({ ...prev, stack_gap: e.target.checked }))} className="accent-[#FFE800]" />
                  Stack gap alerts
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!alertPrefs.renewal_reminder} onChange={(e) => setAlertPrefs((prev) => ({ ...prev, renewal_reminder: e.target.checked }))} className="accent-[#FFE800]" />
                  Renewal reminders
                </label>
                <label className="flex items-center gap-2 md:col-span-2">
                  <input type="checkbox" checked={!!alertPrefs.newsletter_subscription} onChange={(e) => setAlertPrefs((prev) => ({ ...prev, newsletter_subscription: e.target.checked }))} className="accent-[#FFE800]" />
                  Newsletter subscription
                </label>
              </div>
              <label className="label-pro">Alert email</label>
              <input
                type="email"
                value={alertPrefs.email || ''}
                onChange={(e) => setAlertPrefs((prev) => ({ ...prev, email: e.target.value }))}
                className="input-pro rounded-none"
                placeholder="alerts@yourcompany.com"
              />
              <button onClick={saveAlertPreferences} disabled={savingAlerts} className="mt-4 px-4 py-2 border disabled:opacity-60" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}>
                {savingAlerts ? 'Saving...' : 'Save Alert Preferences'}
              </button>
            </div>
          </div>
        )}

        <StackConsultingPrime />
      </section>
    </div>
  );
};

export default StackDashboardPage;

